import * as Sentry from '@sentry/react';

const DEBUG_MODE = import.meta.env.VITE_DEBUG_MODE === 'true' || import.meta.env.DEV;

export interface LogContext {
  component?: string;
  userId?: string;
  [key: string]: any;
}

/**
 * Centralized logger that respects debug mode settings
 * - Only logs in development OR when VITE_DEBUG_MODE=true
 * - Integrates with Sentry for error-level logs
 * - Zero runtime cost when disabled
 */
export const logger = {
  /**
   * Debug-level logging - only shown when debug mode is enabled
   */
  debug: (message: string, context?: LogContext) => {
    if (DEBUG_MODE) {
      console.log(`[DEBUG] ${message}`, context || '');
    }

    // Add breadcrumb to Sentry for debugging context
    if (typeof Sentry !== 'undefined' && Sentry.addBreadcrumb) {
      Sentry.addBreadcrumb({
        category: 'debug',
        message,
        level: 'debug',
        data: context,
      });
    }
  },

  /**
   * Info-level logging - important informational messages
   */
  info: (message: string, context?: LogContext) => {
    if (DEBUG_MODE) {
      console.info(`[INFO] ${message}`, context || '');
    }

    if (typeof Sentry !== 'undefined' && Sentry.addBreadcrumb) {
      Sentry.addBreadcrumb({
        category: 'info',
        message,
        level: 'info',
        data: context,
      });
    }
  },

  /**
   * Warning-level logging - potential issues
   */
  warn: (message: string, context?: LogContext) => {
    if (DEBUG_MODE) {
      console.warn(`[WARN] ${message}`, context || '');
    }

    if (typeof Sentry !== 'undefined' && Sentry.addBreadcrumb) {
      Sentry.addBreadcrumb({
        category: 'warning',
        message,
        level: 'warning',
        data: context,
      });
    }
  },

  /**
   * Error-level logging - errors and exceptions
   * Always logs to console in production and sends to Sentry
   */
  error: (message: string, context?: LogContext) => {
    console.error(`[ERROR] ${message}`, context || '');

    // Send to Sentry if available
    if (typeof Sentry !== 'undefined' && Sentry.captureException) {
      let error: Error;
      const contextError = context?.error;

      if (contextError instanceof Error) {
        // Already an Error instance, use it directly
        error = contextError;
      } else if (contextError && typeof contextError === 'object') {
        // Supabase/PostgreSQL error object with code, message, details, hint
        // Wrap it in an Error instance
        const errorMessage = (contextError as { message?: string }).message || message;
        error = new Error(errorMessage);
        // Attach original error properties to the Error object
        Object.assign(error, {
          code: (contextError as { code?: string }).code,
          details: (contextError as { details?: string }).details,
          hint: (contextError as { hint?: string }).hint,
        });
      } else {
        error = new Error(message);
      }

      Sentry.captureException(error, {
        extra: context,
      });
    }
  },
};
